import { DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import * as api from '@apis/campaigns';
import FileUploadButton from '@components/common/FileUploadButton';
import { SelectDiscountCouponModal } from '@components/discount-coupon/SelectDiscountCouponModal';
import SelectProductModal from '@components/product/SelectProductModal';
import SelectProductCollectionModal from '@components/product-collection/SelectProductCollectionModal';
import { useAuthentication } from '@hooks/authentication';
import { Campaign } from '@models/campaign';
import { DiscountCoupon } from '@models/discount-coupon';
import { ProductModel } from '@models/product';
import { ProductCollection } from '@models/productCollection';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery } from '@tanstack/react-query';
import { removeTempProperties } from '@utils/dataTransform';
import { cdnImageUrl, resizeImage } from '@utils/image';
import { extractFileName } from '@utils/string';
import { Button, DatePicker, Input, message, Modal, Switch, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { RcFile } from 'antd/es/upload';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { CAMPAIGN_SHARE_BASE_URL } from '@variables';
import { toast } from 'react-toastify';
import CropFileUploadButton from '@components/common/CropFileUploadButton';

const IMAGE_ACCEPT = ['jpg', 'jpeg', 'png', 'tif', 'tiff', 'webp', 'svg'].map((ext) => `.${ext}`).join(',');

type CampaignReviewItemForm = {
  title: string;
  description: string;
  link: string;
  imageUrl?: string;
  imageUrlTemp?: string;
};

const emptyReviewItem = (): CampaignReviewItemForm => ({
  title: '',
  description: '',
  link: '',
  imageUrl: '',
});

function reviewCategoryItemsFromApi(
  item: NonNullable<NonNullable<Campaign['details']>['reviewCategories']>[number],
): CampaignReviewItemForm[] {
  const revs = item.reviews;
  if (revs && revs.length > 0) {
    return revs.map((r) => ({
      title: r.title || '',
      description: r.description || '',
      imageUrl: r.imageUrl || '',
      link: r.link || '',
    }));
  }
  return (item.links || [])
    .map((link) => ({
      title: '',
      description: '',
      imageUrl: '',
      link: typeof link === 'string' ? link.trim() : '',
    }))
    .filter((r) => r.link);
}

function countFilledReviewItems(reviews: CampaignReviewItemForm[] | undefined): number {
  return (reviews || []).filter(
    (r) => (r.title || '').trim() || (r.description || '').trim() || (r.link || '').trim() || (r.imageUrl || '').trim(),
  ).length;
}

interface SortableLinkedCollectionItemProps {
  id: string;
  title: string;
  onRemove: () => void;
}

const SortableLinkedCollectionItem: React.FC<SortableLinkedCollectionItemProps> = ({ id, title, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.88 : 1,
  };
  const hasTitle = !!(title || '').trim();
  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        border: '1px solid #e0e6ef',
        borderRadius: 8,
        background: '#fff',
        maxWidth: '100%',
      }}
      {...attributes}>
      <span
        {...listeners}
        style={{ cursor: 'grab', color: '#999', display: 'inline-flex', touchAction: 'none' }}
        title="드래그하여 순서 변경">
        <HolderOutlined />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 500,
            fontSize: 14,
            color: hasTitle ? '#333' : '#999',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={hasTitle ? title : undefined}>
          {hasTitle ? title : '제목 없음'}
          <span style={{ fontSize: 12, color: '#999', wordBreak: 'break-all', marginLeft: 4 }}>({id})</span>
        </div>
      </div>
      <Button size="small" danger onClick={onRemove}>
        삭제
      </Button>
    </div>
  );
};

type CampaignForm = Omit<Partial<Campaign>, 'details'> & {
  imagesTemp?: string[];
  coverImageUrlTemp?: string;
  ogImageUrlTemp?: string;
  couponImageUrlTemp?: string;
  details?: {
    reviewCategories?: Array<{
      label: string;
      imageUrl?: string;
      imageUrlTemp?: string;
      reviews: CampaignReviewItemForm[];
    }>;
    locations?: Array<{
      label: string;
      imageUrl?: string;
      imageUrlTemp?: string;
    }>;
    contents?: Array<{
      title: string;
      images: string[];
      imagesTemp?: string[];
    }>;
  };
};

interface CampaignEditModalProps {
  open?: boolean;
  data?: Campaign;
  onClose?: () => void;
  needRefetch?: () => void;
}

const CampaignEditModal: React.FC<CampaignEditModalProps> = (props) => {
  const dialog = useOkCancelDialog();
  const [data, setData] = useState<CampaignForm>();
  const [modifiedFiles, setModifiedFiles] = useState<{ [key: string]: File }>({});

  useEffect(() => {
    if (props.open) {
      setData(
        props.data
          ? {
              ...props.data,
              imagesTemp: [],
              active: props.data.active !== false,
              details: {
                reviewCategories: (props.data.details?.reviewCategories || []).map((item) => ({
                  label: item.label || '',
                  imageUrl: item.imageUrl || '',
                  reviews: reviewCategoryItemsFromApi(item),
                })),
                locations: (props.data.details?.locations || []).map((item) => ({
                  label: item.label || '',
                  imageUrl: item.imageUrl || '',
                })),
                contents: (props.data.details?.contents || []).map((item) => ({
                  title: item.title || '',
                  images: item.images || [],
                  imagesTemp: [],
                })),
              },
              precautions: (props.data.precautions || []).map((item) => ({
                title: item.title || '',
                description: item.description || '',
              })),
            }
          : {
              title: '',
              description: '',
              active: false,
              startDate: undefined,
              endDate: undefined,
              mainCopy: '',
              subCopy: '',
              ogImageUrl: '',
              ogImageUrlTemp: '',
              coverImageUrl: '',
              coverImageUrlTemp: '',
              couponImageUrl: '',
              couponImageUrlTemp: '',
              images: [],
              imagesTemp: [],
              reward: '',
              collectionIds: [],
              couponIds: [],
              productIds: [],
              socialContentLinks: [],
              details: {
                reviewCategories: [],
                locations: [],
                contents: [],
              },
              precautions: [],
            },
      );
      setModifiedFiles({});
    }
  }, [props.open, props.data]);

  const createMutation = useMutation({
    mutationFn: api.createCampaign,
    onSuccess: async () => {
      props.onClose?.();
      dialog.open({ content: '추가했습니다.', type: 'ok' });
      props.needRefetch?.();
    },
    onError: async (e: any) => {
      dialog.open({ title: '오류', content: e?.response?.data?.message, type: 'ok' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ campaignId, formData }: { campaignId: string; formData: FormData }) =>
      api.updateCampaign(campaignId, formData),
    onSuccess: async () => {
      props.onClose?.();
      dialog.open({ content: '수정했습니다.', type: 'ok' });
      props.needRefetch?.();
    },
    onError: async (e: any) => {
      dialog.open({ title: '오류', content: e?.response?.data?.message, type: 'ok' });
    },
  });

  const onUploadImages = async (files: FileList) => {
    const list = Array.from(files).slice(0, 10);
    if (list.length === 0) return;

    const resized = await Promise.all(list.map(async (file) => await resizeImage(file)));

    const nextImageNames: string[] = [];
    const nextTempUrls: string[] = [];
    const nextFiles: { [key: string]: File } = {};

    resized.forEach((result, index) => {
      const { fileName } = extractFileName(list[index].name);
      const contentTypes = result.type.split('/');
      const ext = contentTypes[1] || 'webp';
      const uploadFileName = `${fileName}.${ext}`;
      const newImageFile = new File([result.image], uploadFileName) as RcFile;
      const tempUrl = URL.createObjectURL(result.image);

      nextFiles[uploadFileName] = newImageFile;
      nextImageNames.push(uploadFileName);
      nextTempUrls.push(tempUrl);
    });

    setModifiedFiles((prev) => ({ ...prev, ...nextFiles }));
    setData((prev) => {
      const currentImages = prev?.images || [];
      const currentTemp = prev?.imagesTemp || [];
      return {
        ...(prev || {}),
        images: [...currentImages, ...nextImageNames],
        imagesTemp: [...currentTemp, ...nextTempUrls],
      };
    });
  };

  const onDeleteImage = (index: number) => {
    const imageName = data?.images?.[index];
    setData((prev) => {
      const currentImages = prev?.images || [];
      const currentTemp = prev?.imagesTemp || [];
      return {
        ...(prev || {}),
        images: currentImages.filter((_, i) => i !== index),
        imagesTemp: currentTemp.filter((_, i) => i !== index),
      };
    });

    if (imageName && modifiedFiles[imageName]) {
      setModifiedFiles((prev) => {
        const next = { ...prev };
        delete next[imageName];
        return next;
      });
    }
  };

  const onChangeSingleImage = async (files: FileList, key: 'coverImageUrl' | 'couponImageUrl' | 'ogImageUrl') => {
    const { fileName } = extractFileName(files[0].name);
    const resized = await resizeImage(files[0], { maxWidth: 856 });
    const contentTypes = resized.type.split('/');
    const uploadFileName = `${fileName}.${contentTypes[1] || 'webp'}`;
    const newImageFile = new File([resized.image], uploadFileName) as RcFile;
    const tempUrl = URL.createObjectURL(resized.image);

    setData((prev) => ({
      ...(prev || {}),
      [key]: uploadFileName,
      [`${key}Temp`]: tempUrl,
    }));
    setModifiedFiles((prev) => ({ ...prev, [uploadFileName]: newImageFile }));
  };

  const addReviewCategory = () => {
    setData((prev) => ({
      ...(prev || {}),
      details: {
        ...(prev?.details || {}),
        reviewCategories: [
          ...(prev?.details?.reviewCategories || []),
          {
            label: '',
            imageUrl: '',
            reviews: [],
          },
        ],
      },
    }));
  };

  const updateReviewCategory = (
    index: number,
    patch: Partial<{ label: string; imageUrl?: string; imageUrlTemp?: string; reviews: CampaignReviewItemForm[] }>,
  ) => {
    setData((prev) => {
      const categories = [...(prev?.details?.reviewCategories || [])];
      categories[index] = { ...(categories[index] || { label: '', reviews: [] }), ...patch };
      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          reviewCategories: categories,
        },
      };
    });
  };

  const removeReviewCategory = (index: number) => {
    setReviewsEditorCategoryIndex((editing) => {
      if (editing === null) return null;
      if (editing === index) return null;
      if (editing > index) return editing - 1;
      return editing;
    });
    setData((prev) => {
      const categories = [...(prev?.details?.reviewCategories || [])];
      categories.splice(index, 1);
      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          reviewCategories: categories,
        },
      };
    });
  };

  const onChangeReviewCategoryImage = async (files: FileList, index: number) => {
    const { fileName } = extractFileName(files[0].name);
    const resized = await resizeImage(files[0], { maxWidth: 856 });
    const contentTypes = resized.type.split('/');
    const uploadFileName = `${fileName}.${contentTypes[1] || 'webp'}`;
    const newImageFile = new File([resized.image], uploadFileName) as RcFile;
    const tempUrl = URL.createObjectURL(resized.image);

    updateReviewCategory(index, { imageUrl: uploadFileName, imageUrlTemp: tempUrl });
    setModifiedFiles((prev) => ({ ...prev, [uploadFileName]: newImageFile }));
  };

  const updateReviewCategoryReview = (
    categoryIndex: number,
    reviewIndex: number,
    patch: Partial<CampaignReviewItemForm>,
  ) => {
    setData((prev) => {
      const categories = [...(prev?.details?.reviewCategories || [])];
      const prevCat = categories[categoryIndex] || { label: '', reviews: [] as CampaignReviewItemForm[] };
      const cat = { ...prevCat };
      const reviews = [...(cat.reviews || [])];
      const base = reviews[reviewIndex] || emptyReviewItem();
      reviews[reviewIndex] = { ...base, ...patch };
      cat.reviews = reviews;
      categories[categoryIndex] = cat;
      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          reviewCategories: categories,
        },
      };
    });
  };

  const addReviewToCategory = (categoryIndex: number) => {
    setData((prev) => {
      const categories = [...(prev?.details?.reviewCategories || [])];
      const prevCat = categories[categoryIndex] || { label: '', reviews: [] as CampaignReviewItemForm[] };
      const cat = { ...prevCat };
      cat.reviews = [...(cat.reviews || []), emptyReviewItem()];
      categories[categoryIndex] = cat;
      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          reviewCategories: categories,
        },
      };
    });
  };

  const removeReviewFromCategory = (categoryIndex: number, reviewIndex: number) => {
    setData((prev) => {
      const categories = [...(prev?.details?.reviewCategories || [])];
      const prevCat = categories[categoryIndex];
      if (!prevCat) return prev || {};
      const reviews = [...(prevCat.reviews || [])];
      reviews.splice(reviewIndex, 1);
      categories[categoryIndex] = { ...prevCat, reviews };
      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          reviewCategories: categories,
        },
      };
    });
  };

  const onChangeReviewItemImage = async (files: FileList, categoryIndex: number, reviewIndex: number) => {
    const { fileName } = extractFileName(files[0].name);
    const resized = await resizeImage(files[0], { maxWidth: 856 });
    const contentTypes = resized.type.split('/');
    const ext = contentTypes[1] || 'webp';
    const uploadFileName = `review-c${categoryIndex}-r${reviewIndex}-${fileName}.${ext}`;
    const newImageFile = new File([resized.image], uploadFileName) as RcFile;
    const tempUrl = URL.createObjectURL(resized.image);

    updateReviewCategoryReview(categoryIndex, reviewIndex, { imageUrl: uploadFileName, imageUrlTemp: tempUrl });
    setModifiedFiles((prev) => ({ ...prev, [uploadFileName]: newImageFile }));
  };

  const addLocation = () => {
    setData((prev) => ({
      ...(prev || {}),
      details: {
        ...(prev?.details || {}),
        locations: [...(prev?.details?.locations || []), { label: '', imageUrl: '' }],
      },
    }));
  };

  const updateLocation = (
    index: number,
    patch: Partial<{ label: string; imageUrl?: string; imageUrlTemp?: string }>,
  ) => {
    setData((prev) => {
      const locations = [...(prev?.details?.locations || [])];
      locations[index] = { ...(locations[index] || { label: '', imageUrl: '' }), ...patch };
      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          locations,
        },
      };
    });
  };

  const removeLocation = (index: number) => {
    setData((prev) => {
      const locations = [...(prev?.details?.locations || [])];
      locations.splice(index, 1);
      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          locations,
        },
      };
    });
  };

  const onChangeLocationImage = async (files: FileList, index: number) => {
    const { fileName } = extractFileName(files[0].name);
    const resized = await resizeImage(files[0], { maxWidth: 856 });
    const contentTypes = resized.type.split('/');
    const uploadFileName = `${fileName}.${contentTypes[1] || 'webp'}`;
    const newImageFile = new File([resized.image], uploadFileName) as RcFile;
    const tempUrl = URL.createObjectURL(resized.image);

    updateLocation(index, { imageUrl: uploadFileName, imageUrlTemp: tempUrl });
    setModifiedFiles((prev) => ({ ...prev, [uploadFileName]: newImageFile }));
  };

  const addContent = () => {
    setData((prev) => {
      const contents = prev?.details?.contents || [];
      if (contents.length >= 3) return prev || {};
      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          contents: [...contents, { title: '', images: [], imagesTemp: [] }],
        },
      };
    });
  };

  const updateContent = (index: number, patch: Partial<{ title: string; images: string[]; imagesTemp?: string[] }>) => {
    setData((prev) => {
      const contents = [...(prev?.details?.contents || [])];
      contents[index] = { ...(contents[index] || { title: '', images: [], imagesTemp: [] }), ...patch };
      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          contents,
        },
      };
    });
  };

  const removeContent = (index: number) => {
    const removed = data?.details?.contents?.[index];
    (removed?.images || []).forEach((imageName) => {
      if (!imageName || !modifiedFiles[imageName]) return;
      setModifiedFiles((prev) => {
        const next = { ...prev };
        delete next[imageName];
        return next;
      });
    });

    setData((prev) => {
      const contents = [...(prev?.details?.contents || [])];
      contents.splice(index, 1);
      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          contents,
        },
      };
    });
  };

  const onChangeContentImages = async (files: FileList, index: number) => {
    const list = Array.from(files);
    if (list.length === 0) return;

    const resized = await Promise.all(list.map(async (file) => await resizeImage(file, { maxWidth: 856 })));
    const nextImageNames: string[] = [];
    const nextTempUrls: string[] = [];
    const nextFiles: { [key: string]: File } = {};

    resized.forEach((result, fileIndex) => {
      const { fileName } = extractFileName(list[fileIndex].name);
      const contentTypes = result.type.split('/');
      const ext = contentTypes[1] || 'webp';
      const uploadFileName = `content-c${index}-${Date.now()}-${fileName}.${ext}`;
      const newImageFile = new File([result.image], uploadFileName) as RcFile;
      const tempUrl = URL.createObjectURL(result.image);

      nextFiles[uploadFileName] = newImageFile;
      nextImageNames.push(uploadFileName);
      nextTempUrls.push(tempUrl);
    });

    setModifiedFiles((prev) => ({ ...prev, ...nextFiles }));
    setData((prev) => {
      const contents = [...(prev?.details?.contents || [])];
      const current = contents[index] || { title: '', images: [], imagesTemp: [] };
      contents[index] = {
        ...current,
        images: [...(current.images || []), ...nextImageNames],
        imagesTemp: [...(current.imagesTemp || []), ...nextTempUrls],
      };

      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          contents,
        },
      };
    });
  };

  const removeContentImage = (contentIndex: number, imageIndex: number) => {
    const imageName = data?.details?.contents?.[contentIndex]?.images?.[imageIndex];

    setData((prev) => {
      const contents = [...(prev?.details?.contents || [])];
      const current = contents[contentIndex] || { title: '', images: [], imagesTemp: [] };
      contents[contentIndex] = {
        ...current,
        images: (current.images || []).filter((_, i) => i !== imageIndex),
        imagesTemp: (current.imagesTemp || []).filter((_, i) => i !== imageIndex),
      };

      return {
        ...(prev || {}),
        details: {
          ...(prev?.details || {}),
          contents,
        },
      };
    });

    if (imageName && modifiedFiles[imageName]) {
      setModifiedFiles((prev) => {
        const next = { ...prev };
        delete next[imageName];
        return next;
      });
    }
  };

  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const [reviewsEditorCategoryIndex, setReviewsEditorCategoryIndex] = useState<number | null>(null);

  const addUniqueIds = (prevIds: string[] = [], nextIds: string[] = []) => {
    return Array.from(new Set([...(prevIds || []), ...(nextIds || [])])).filter(Boolean);
  };

  const getCouponId = (coupon: any): string | undefined => {
    if (!coupon) return undefined;
    if (typeof coupon === 'string') return coupon;
    if (typeof coupon === 'object') return coupon.couponId || coupon._id || coupon.id;
    return undefined;
  };

  const addCoupons = (nextCoupons: DiscountCoupon[]) => {
    setData((prev) => {
      const existing = prev?.coupons || [];
      const map = new Map<string, any>();

      existing.forEach((c: any) => {
        const id = getCouponId(c);
        if (id) map.set(id, c);
      });

      nextCoupons.forEach((coupon) => {
        if (!coupon?._id) return;
        map.set(coupon._id, { couponId: coupon._id, imageUrl: '', name: coupon.name });
      });

      return {
        ...(prev || {}),
        couponIds: Array.from(map.keys()),
        coupons: Array.from(map.values()),
      };
    });
  };

  const addPrecaution = () => {
    setData((prev) => ({
      ...(prev || {}),
      precautions: [...(prev?.precautions || []), { title: '', description: '' }],
    }));
  };

  const updatePrecaution = (index: number, patch: Partial<{ title: string; description: string }>) => {
    setData((prev) => {
      const list = [...(prev?.precautions || [])];
      list[index] = { ...(list[index] || { title: '', description: '' }), ...patch };
      return { ...(prev || {}), precautions: list };
    });
  };

  const removePrecaution = (index: number) => {
    setData((prev) => {
      const list = [...(prev?.precautions || [])];
      list.splice(index, 1);
      return { ...(prev || {}), precautions: list };
    });
  };

  const startDateValue = useMemo(() => (data?.startDate ? dayjs(data.startDate as any) : null), [data?.startDate]);
  const endDateValue = useMemo(() => (data?.endDate ? dayjs(data.endDate as any) : null), [data?.endDate]);

  const collectionSortSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  );

  const collectionTitleById = useMemo(() => {
    const m = new Map<string, string>();
    (data?.collections || []).forEach((c: ProductCollection) => {
      if (c?._id) m.set(String(c._id), c.title || '');
    });
    return m;
  }, [data?.collections]);

  const onCollectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setData((prev) => {
      const ids = [...(prev?.collectionIds || [])];
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const nextIds = arrayMove(ids, oldIndex, newIndex);
      const byId = new Map((prev?.collections || []).map((c: ProductCollection) => [c._id, c]));
      const orderedCols = nextIds.map((id) => byId.get(id) ?? ({ _id: id, title: '' } as ProductCollection));
      return { ...prev, collectionIds: nextIds, collections: orderedCols };
    });
  };

  const removeCollectionAt = (index: number) => {
    setData((prev) => {
      const ids = [...(prev?.collectionIds || [])];
      ids.splice(index, 1);
      const idSet = new Set(ids);
      const cols = (prev?.collections || []).filter((c: ProductCollection) => c?._id && idSet.has(c._id));
      const byId = new Map(cols.map((c: ProductCollection) => [c._id, c]));
      const orderedCols = ids.map((id) => byId.get(id) ?? ({ _id: id, title: '' } as ProductCollection));
      return { ...prev, collectionIds: ids, collections: orderedCols };
    });
  };

  const onSave = () => {
    if (!data) return;

    const formData: FormData = new FormData();
    const json = removeTempProperties({
      title: data.title || '',
      description: data.description || '',
      active: !!data.active,
      startDate: data.startDate || null,
      endDate: data.endDate || null,
      mainCopy: data.mainCopy || '',
      subCopy: data.subCopy || '',
      ogImageUrl: data.ogImageUrl || '',
      coverImageUrl: data.coverImageUrl || '',
      couponImageUrl: data.couponImageUrl || '',
      images: data.images || [],
      reward: data.reward || '',
      precautions: (data.precautions || [])
        .map((item) => ({
          title: (item.title || '').trim(),
          description: (item.description || '').trim(),
        }))
        .filter((item) => item.title || item.description),
      collectionIds: data.collectionIds || [],
      couponIds:
        data.couponIds ||
        (data.coupons || []).map((coupon: any) => getCouponId(coupon)).filter((id): id is string => !!id),
      productIds: data.productIds || [],
      socialContentLinks: (data.socialContentLinks || []).map((link) => (link || '').trim()).filter((link) => !!link),
      details: {
        reviewCategories: (data.details?.reviewCategories || []).map((item) => ({
          label: item.label || '',
          imageUrl: item.imageUrl || '',
          reviews: (item.reviews || [])
            .map((r) => ({
              title: (r.title || '').trim(),
              description: (r.description || '').trim(),
              imageUrl: r.imageUrl || '',
              link: (r.link || '').trim(),
            }))
            .filter((r) => r.link || r.title || r.description || r.imageUrl),
        })),
        locations: (data.details?.locations || [])
          .map((item) => ({
            label: (item.label || '').trim(),
            imageUrl: item.imageUrl || '',
          }))
          .filter((item) => item.label || item.imageUrl),
        contents: (data.details?.contents || [])
          .map((item) => ({
            title: (item.title || '').trim(),
            images: (item.images || []).filter(Boolean),
          }))
          .filter((item) => item.title || item.images.length > 0),
      },
    });
    formData.append('json', JSON.stringify(json));
    Object.values(modifiedFiles).forEach((file) => formData.append('files', file));

    if (props.data?._id) {
      updateMutation.mutate({ campaignId: props.data._id, formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const reviewsModalIdx = reviewsEditorCategoryIndex;
  const reviewsModalCategory =
    reviewsModalIdx !== null ? data?.details?.reviewCategories?.[reviewsModalIdx] : undefined;
  const reviewsModalTitle =
    reviewsModalIdx !== null && reviewsModalCategory && (reviewsModalCategory.label || '').trim()
      ? `리뷰 카드 — ${(reviewsModalCategory.label || '').trim()}`
      : '리뷰 카드 편집';
  const reviewsModalList = reviewsModalCategory?.reviews || [];

  return (
    <>
      <SelectProductModal
        open={isProductModalOpen}
        onAddProducts={(products: ProductModel[]) => {
          const ids = products.map((p) => p._id).filter(Boolean) as string[];
          setData((prev) => ({
            ...(prev || {}),
            productIds: addUniqueIds(prev?.productIds || [], ids),
          }));
        }}
        onClose={() => setIsProductModalOpen(false)}
      />
      <SelectProductCollectionModal
        open={isCollectionModalOpen}
        onClose={() => setIsCollectionModalOpen(false)}
        onAddCollections={(collections: ProductCollection[]) => {
          const ids = collections.map((c) => c._id).filter(Boolean) as string[];
          setData((prev) => {
            const nextIds = addUniqueIds(prev?.collectionIds || [], ids);
            const map = new Map<string, ProductCollection>(
              ((prev?.collections || []) as ProductCollection[]).map((c) => [c._id, c]),
            );
            collections.forEach((c) => {
              if (c._id) map.set(c._id, c);
            });
            const orderedCols = nextIds.map((id) => map.get(id) ?? ({ _id: id, title: '' } as ProductCollection));
            return { ...prev, collectionIds: nextIds, collections: orderedCols };
          });
        }}
        existingIds={data?.collectionIds || []}
      />
      <SelectDiscountCouponModal
        open={isCouponModalOpen}
        onClose={() => setIsCouponModalOpen(false)}
        onAddCoupons={(coupons: DiscountCoupon[]) => addCoupons(coupons)}
      />

      <Modal
        width={1180}
        style={{ top: 24 }}
        styles={{
          footer: { height: '44px' },
          body: { maxHeight: 'calc(100vh - 120px)', overflow: 'hidden', padding: '12px 24px 20px' },
        }}
        open={props.open}
        title={props.data?._id ? '캠페인 수정' : '신규 캠페인 생성'}
        onCancel={props.onClose}
        footer={
          <div style={{ width: '100%' }}>
            <Button style={{ width: 120 }} onClick={props.onClose}>
              취소
            </Button>
            <Button
              type="primary"
              style={{ width: 120, marginLeft: '8px' }}
              loading={createMutation.isPending || updateMutation.isPending}
              onClick={onSave}>
              {props.data?._id ? '수정하기' : '생성하기'}
            </Button>
          </div>
        }>
        <div
          style={{
            display: 'flex',
            gap: 20,
            alignItems: 'stretch',
            minHeight: 0,
            maxHeight: 'calc(100vh - 200px)',
          }}>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              overflowY: 'auto',
              paddingRight: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontWeight: 600 }}>활성화</div>
                <Switch
                  checked={!!data?.active}
                  onChange={(checked) => setData((prev) => ({ ...(prev || {}), active: checked }))}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                />
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>기간</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 240, flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>시작일</div>
                  <DatePicker
                    value={startDateValue}
                    showTime={{ showSecond: false }}
                    style={{ width: '100%' }}
                    onChange={(value, dateString) => {
                      setData((prev) => ({
                        ...(prev || {}),
                        startDate: dateString ? moment(dateString, 'YYYY-MM-DD HH:mm').toDate() : undefined,
                      }));
                    }}
                  />
                </div>
                <div style={{ minWidth: 240, flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>종료일</div>
                  <DatePicker
                    value={endDateValue}
                    showTime={{ showSecond: false }}
                    style={{ width: '100%' }}
                    onChange={(value, dateString) => {
                      setData((prev) => ({
                        ...(prev || {}),
                        endDate: dateString ? moment(dateString, 'YYYY-MM-DD HH:mm').toDate() : undefined,
                      }));
                    }}
                  />
                </div>
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>제목</div>
              <Input
                value={data?.title || ''}
                placeholder="캠페인 제목"
                onChange={(e) => setData((prev) => ({ ...(prev || {}), title: e.target.value }))}
              />
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>설명(og description)</div>
              <Input.TextArea
                value={data?.description || ''}
                placeholder="캠페인 설명"
                rows={3}
                onChange={(e) => setData((prev) => ({ ...(prev || {}), description: e.target.value }))}
              />
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>메인 카피</div>
              <Input.TextArea
                value={data?.mainCopy || ''}
                rows={4}
                onChange={(e) => setData((prev) => ({ ...(prev || {}), mainCopy: e.target.value }))}
              />
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>서브 카피</div>
              <Input.TextArea
                value={data?.subCopy || ''}
                rows={3}
                onChange={(e) => setData((prev) => ({ ...(prev || {}), subCopy: e.target.value }))}
              />
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>OG 이미지</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <CropFileUploadButton
                  accept={IMAGE_ACCEPT}
                  style={{
                    fontSize: '14px',
                    height: '36px',
                    padding: '4px 15px',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d9d9d9',
                    color: 'rgba(0, 0, 0, 0.88)',
                    boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                    cursor: 'pointer',
                  }}
                  modalTitleChoice="OG 이미지 선택"
                  modalTitleCrop="OG 이미지 크롭"
                  onChangeFiles={(files) => {
                    if (files) onChangeSingleImage(files, 'ogImageUrl');
                  }}>
                  OG 이미지 선택
                </CropFileUploadButton>
                <Button
                  danger
                  onClick={() =>
                    setData((prev) => ({
                      ...(prev || {}),
                      ogImageUrl: '',
                      ogImageUrlTemp: '',
                    }))
                  }>
                  제거
                </Button>
              </div>
              {(data?.ogImageUrlTemp || data?.ogImageUrl) && (
                <img
                  src={cdnImageUrl(data?.ogImageUrlTemp || data?.ogImageUrl)}
                  style={{ width: 240, height: 140, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }}
                />
              )}
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>커버 이미지</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <CropFileUploadButton
                  accept={IMAGE_ACCEPT}
                  style={{
                    fontSize: '14px',
                    height: '36px',
                    padding: '4px 15px',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d9d9d9',
                    color: 'rgba(0, 0, 0, 0.88)',
                    boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                    cursor: 'pointer',
                  }}
                  modalTitleChoice="커버 이미지 선택"
                  modalTitleCrop="커버 이미지 크롭"
                  onChangeFiles={(files) => {
                    if (files) onChangeSingleImage(files, 'coverImageUrl');
                  }}>
                  커버 이미지 선택
                </CropFileUploadButton>
                <Button
                  danger
                  onClick={() =>
                    setData((prev) => ({
                      ...(prev || {}),
                      coverImageUrl: '',
                      coverImageUrlTemp: '',
                    }))
                  }>
                  제거
                </Button>
              </div>
              {(data?.coverImageUrlTemp || data?.coverImageUrl) && (
                <img
                  src={cdnImageUrl(data?.coverImageUrlTemp || data?.coverImageUrl)}
                  style={{ width: 240, height: 140, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }}
                />
              )}
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>이미지</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <FileUploadButton
                  multiple
                  accept={IMAGE_ACCEPT}
                  style={{
                    fontSize: '14px',
                    height: '36px',
                    padding: '4px 15px',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d9d9d9',
                    color: 'rgba(0, 0, 0, 0.88)',
                    boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                    cursor: 'pointer',
                  }}
                  onChange={(e) => {
                    if (e.target.files) onUploadImages(e.target.files);
                  }}>
                  이미지 추가
                </FileUploadButton>
                <div style={{ color: '#999', fontSize: 12 }}>{(data?.images || []).length}개</div>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {(data?.images || []).map((imageName, index) => {
                  const tempUrl = data?.imagesTemp?.[index];
                  const src = tempUrl || cdnImageUrl(imageName);
                  return (
                    <div key={`${imageName}-${index}`} style={{ width: 180 }}>
                      <div
                        style={{
                          width: 180,
                          height: 100,
                          border: '1px solid #eaeaea',
                          borderRadius: 8,
                          overflow: 'hidden',
                          backgroundColor: '#fafafa',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}>
                        {src ? (
                          <img src={src} alt="campaign" style={{ width: 180, height: 100, objectFit: 'cover' }} />
                        ) : (
                          'NO IMAGE'
                        )}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onDeleteImage(index)}>
                          삭제
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {(!data?.images || data.images.length === 0) && (
                  <div style={{ color: '#999', padding: '8px 0' }}>이미지가 없습니다.</div>
                )}
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>쿠폰 이미지</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <FileUploadButton
                  accept={IMAGE_ACCEPT}
                  style={{
                    fontSize: '14px',
                    height: '36px',
                    padding: '4px 15px',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d9d9d9',
                    color: 'rgba(0, 0, 0, 0.88)',
                    boxShadow: '0 2px 0 rgba(0, 0, 0, 0.02)',
                    cursor: 'pointer',
                  }}
                  onChange={(e) => {
                    if (e.target.files) onChangeSingleImage(e.target.files, 'couponImageUrl');
                  }}>
                  이미지 선택
                </FileUploadButton>
                <Button
                  danger
                  onClick={() =>
                    setData((prev) => ({
                      ...(prev || {}),
                      couponImageUrl: '',
                      couponImageUrlTemp: '',
                    }))
                  }>
                  제거
                </Button>
              </div>
              {(data?.couponImageUrlTemp || data?.couponImageUrl) && (
                <img
                  src={cdnImageUrl(data?.couponImageUrlTemp || data?.couponImageUrl)}
                  style={{ width: 240, height: 140, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' }}
                />
              )}
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>리워드</div>
              <Input.TextArea
                value={data?.reward || ''}
                rows={4}
                onChange={(e) => setData((prev) => ({ ...(prev || {}), reward: e.target.value }))}
              />
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>연동 컬렉션</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Button onClick={() => setIsCollectionModalOpen(true)}>컬렉션 선택</Button>
                <div style={{ color: '#999', fontSize: 12 }}>{(data?.collectionIds || []).length}개 선택</div>
              </div>
              <div style={{ color: '#999', fontSize: 12, marginBottom: 8 }}>
                핸들(⋮)을 드래그하면 순서를 바꿀 수 있습니다. 위에서부터 앱에 노출 순서와 동일합니다.
              </div>
              {(data?.collectionIds || []).length > 0 ? (
                <DndContext
                  sensors={collectionSortSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onCollectionDragEnd}>
                  <SortableContext items={data?.collectionIds || []} strategy={verticalListSortingStrategy}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        padding: 12,
                        border: '1px solid #c5d0e0',
                        borderRadius: 10,
                        background: 'linear-gradient(180deg, #eef3fa 0%, #e6edf7 100%)',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)',
                        boxSizing: 'border-box',
                      }}>
                      {(data?.collectionIds || []).map((id, index) => (
                        <SortableLinkedCollectionItem
                          key={id}
                          id={id}
                          title={collectionTitleById.get(id) || ''}
                          onRemove={() => removeCollectionAt(index)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div style={{ color: '#999', fontSize: 12 }}>선택된 컬렉션이 없습니다.</div>
              )}
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>연동 상품</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Button onClick={() => setIsProductModalOpen(true)}>상품 선택</Button>
                <div style={{ color: '#999', fontSize: 12 }}>{(data?.productIds || []).length}개 선택</div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {(data?.productIds || []).map((id, index) => (
                  <div key={`${id}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 13, color: '#333' }}>{id}</div>
                    <Button
                      size="small"
                      danger
                      onClick={() =>
                        setData((prev) => ({
                          ...(prev || {}),
                          productIds: (prev?.productIds || []).filter((_, i) => i !== index),
                        }))
                      }>
                      삭제
                    </Button>
                  </div>
                ))}
                {(!data?.productIds || data.productIds.length === 0) && (
                  <div style={{ color: '#999', fontSize: 12 }}>선택된 상품이 없습니다.</div>
                )}
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>쿠폰 연동</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Button onClick={() => setIsCouponModalOpen(true)}>쿠폰 선택</Button>
                <div style={{ color: '#999', fontSize: 12 }}>{(data?.couponIds || []).length}개 선택</div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {(data?.couponIds || []).map((couponId, index) => {
                  const coupon = (data?.coupons || []).find((item: any) => getCouponId(item) === couponId);
                  const name = (coupon && typeof coupon === 'object' && 'name' in coupon && (coupon as any).name) || '';
                  return (
                    <div key={`${couponId}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontSize: 13, color: '#333' }}>{name ? `${name} (${couponId})` : couponId}</div>
                      <Button
                        size="small"
                        danger
                        onClick={() =>
                          setData((prev) => ({
                            ...(prev || {}),
                            couponIds: (prev?.couponIds || []).filter((_, i) => i !== index),
                            coupons: (prev?.coupons || []).filter((item: any) => getCouponId(item) !== couponId),
                          }))
                        }>
                        삭제
                      </Button>
                    </div>
                  );
                })}
                {(!data?.couponIds || data.couponIds.length === 0) && (
                  <div style={{ color: '#999', fontSize: 12 }}>선택된 쿠폰이 없습니다.</div>
                )}
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>장소 카테고리</div>
              <div style={{ marginBottom: 8 }}>
                <Button onClick={addLocation}>장소 추가</Button>
              </div>
              {(!data?.details?.locations || data.details.locations.length === 0) && (
                <div style={{ color: '#999', fontSize: 12 }}>등록된 장소가 없습니다.</div>
              )}
              {!!data?.details?.locations?.length && (
                <div
                  style={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    paddingBottom: 8,
                    marginBottom: 4,
                    maxWidth: '100%',
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 10,
                      width: 'max-content',
                      padding: '2px 2px 6px',
                    }}>
                    {(data.details?.locations || []).map((loc, index) => (
                      <div
                        key={`campaign-location-${index}`}
                        style={{
                          flex: '0 0 auto',
                          width: 220,
                          border: '1px solid #e5e5e5',
                          borderRadius: 8,
                          padding: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          background: '#fff',
                        }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12, color: '#666' }}>· {index + 1}</span>
                          <Button danger size="small" onClick={() => removeLocation(index)}>
                            삭제
                          </Button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ fontSize: 12, color: '#666' }}>라벨</div>
                          <Input
                            size="small"
                            value={loc.label}
                            placeholder="장소 이름"
                            onChange={(e) => updateLocation(index, { label: e.target.value })}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ fontSize: 12, color: '#666' }}>이미지</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            <FileUploadButton
                              accept={IMAGE_ACCEPT}
                              style={{
                                fontSize: '12px',
                                height: '28px',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #d9d9d9',
                                color: 'rgba(0, 0, 0, 0.88)',
                                cursor: 'pointer',
                              }}
                              onChange={(e) => {
                                if (e.target.files) onChangeLocationImage(e.target.files, index);
                              }}>
                              선택
                            </FileUploadButton>
                            <Button
                              danger
                              size="small"
                              onClick={() =>
                                updateLocation(index, {
                                  imageUrl: '',
                                  imageUrlTemp: '',
                                })
                              }>
                              제거
                            </Button>
                          </div>
                        </div>
                        {(loc.imageUrlTemp || loc.imageUrl) && (
                          <img
                            src={cdnImageUrl(loc.imageUrlTemp || loc.imageUrl)}
                            style={{
                              width: 112,
                              height: 63,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid #eee',
                              alignSelf: 'flex-start',
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>리뷰 카테고리</div>
              <div style={{ marginBottom: 8 }}>
                <Button onClick={addReviewCategory}>리뷰 카테고리 추가</Button>
              </div>
              {(!data?.details?.reviewCategories || data.details.reviewCategories.length === 0) && (
                <div style={{ color: '#999', fontSize: 12 }}>추가된 리뷰 카테고리가 없습니다.</div>
              )}
              {!!data?.details?.reviewCategories?.length && (
                <div
                  style={{
                    containerType: 'inline-size',
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    paddingBottom: 8,
                    marginBottom: 4,
                    maxWidth: '100%',
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 10,
                      width: 'max-content',
                      padding: '2px 2px 6px',
                    }}>
                    {(data.details?.reviewCategories || []).map((category, index) => (
                      <div
                        key={`review-category-${index}`}
                        style={{
                          boxSizing: 'border-box',
                          flex: '0 0 auto',
                          width: '50cqw',
                          border: '1px solid #e5e5e5',
                          borderRadius: 8,
                          padding: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10,
                          background: '#fff',
                        }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <div></div>
                          <Button danger size="small" onClick={() => removeReviewCategory(index)}>
                            삭제
                          </Button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ fontSize: 12, color: '#666' }}>라벨</div>
                          <Input
                            size="small"
                            value={category.label}
                            placeholder="카테고리 라벨"
                            onChange={(e) => updateReviewCategory(index, { label: e.target.value })}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ fontSize: 12, color: '#666' }}>이미지</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            <FileUploadButton
                              accept={IMAGE_ACCEPT}
                              style={{
                                fontSize: '12px',
                                height: '28px',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #d9d9d9',
                                color: 'rgba(0, 0, 0, 0.88)',
                                cursor: 'pointer',
                              }}
                              onChange={(e) => {
                                if (e.target.files) onChangeReviewCategoryImage(e.target.files, index);
                              }}>
                              선택
                            </FileUploadButton>
                            <Button
                              danger
                              size="small"
                              onClick={() =>
                                updateReviewCategory(index, {
                                  imageUrl: '',
                                  imageUrlTemp: '',
                                })
                              }>
                              제거
                            </Button>
                          </div>
                        </div>
                        {(category.imageUrlTemp || category.imageUrl) && (
                          <img
                            src={cdnImageUrl(category.imageUrlTemp || category.imageUrl)}
                            style={{
                              width: 90,
                              height: 50,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid #eee',
                              alignSelf: 'flex-start',
                            }}
                          />
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ fontSize: 12, color: '#666' }}>
                            리뷰 {countFilledReviewItems(category.reviews)}개
                          </div>
                          <Button size="small" type="primary" onClick={() => setReviewsEditorCategoryIndex(index)}>
                            리뷰 카드 편집
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>콘텐츠 ({data?.details?.contents?.length || 0}/3)</div>
              <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button onClick={addContent} disabled={(data?.details?.contents?.length || 0) >= 3}>
                  콘텐츠 추가
                </Button>
                <span style={{ fontSize: 12, color: '#999' }}>최대 3개까지 등록할 수 있습니다.</span>
              </div>
              {(!data?.details?.contents || data.details.contents.length === 0) && (
                <div style={{ color: '#999', fontSize: 12 }}>등록된 콘텐츠가 없습니다.</div>
              )}
              {!!data?.details?.contents?.length && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}>
                  {(data.details?.contents || []).map((content, index) => (
                    <div
                      key={`campaign-content-${index}`}
                      style={{
                        border: '1px solid #e5e5e5',
                        borderRadius: 8,
                        padding: 12,
                        background: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 12, color: '#666' }}>· {index + 1}</span>
                        <Button danger size="small" onClick={() => removeContent(index)}>
                          삭제
                        </Button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ fontSize: 12, color: '#666' }}>제목</div>
                        <Input
                          size="small"
                          value={content.title}
                          placeholder="콘텐츠 제목"
                          onChange={(e) => updateContent(index, { title: e.target.value })}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ fontSize: 12, color: '#666' }}>이미지</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          <FileUploadButton
                            accept={IMAGE_ACCEPT}
                            multiple
                            style={{
                              fontSize: '12px',
                              height: '28px',
                              padding: '2px 8px',
                              borderRadius: '6px',
                              backgroundColor: '#ffffff',
                              border: '1px solid #d9d9d9',
                              color: 'rgba(0, 0, 0, 0.88)',
                              cursor: 'pointer',
                            }}
                            onChange={(e) => {
                              if (e.target.files) onChangeContentImages(e.target.files, index);
                            }}>
                            이미지 추가
                          </FileUploadButton>
                        </div>
                        {!(content.images || []).length && (
                          <div style={{ color: '#999', fontSize: 12 }}>등록된 이미지가 없습니다.</div>
                        )}
                        {!!content.images?.length && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                            {(content.images || []).map((image, imageIndex) => (
                              <div
                                key={`campaign-content-image-${index}-${imageIndex}`}
                                style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                                <img
                                  src={cdnImageUrl(content.imagesTemp?.[imageIndex] || image)}
                                  style={{
                                    width: 112,
                                    height: 63,
                                    objectFit: 'cover',
                                    borderRadius: 6,
                                    border: '1px solid #eee',
                                  }}
                                />
                                <Button danger size="small" onClick={() => removeContentImage(index, imageIndex)}>
                                  이미지 삭제
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* <div>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>
                소셜 링크 ({(data?.socialContentLinks || []).filter((link) => !!link?.trim()).length}개)
              </div>
              <Input.TextArea
                rows={5}
                placeholder="한 줄에 URL 하나씩 입력 (Enter로 다음 줄)"
                value={(data?.socialContentLinks || []).join('\n')}
                onChange={(e) =>
                  setData((prev) => ({
                    ...(prev || {}),
                    socialContentLinks: e.target.value.split('\n').map((item) => item.trim()),
                  }))
                }
                onKeyDown={(e) => e.stopPropagation()}
              />
              <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>빈 줄은 저장 시 제외됩니다.</div>
            </div> */}
          </div>

          <div
            style={{
              flex: '0 0 380px',
              width: 380,
              maxWidth: '38%',
              minWidth: 260,
              alignSelf: 'stretch',
              borderLeft: '1px solid #f0f0f0',
              paddingLeft: 16,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', textAlign: 'left' }}>
              <div style={{ marginTop: 4 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>유의사항</div>
                <div style={{ marginTop: 8 }}>
                  {(data?.precautions || []).map((item, index) => (
                    <div
                      key={`precaution-${index}`}
                      style={{ marginBottom: 16, padding: 12, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 'bold' }}>제목</span>
                        <Input
                          placeholder="유의사항 제목을 입력하세요"
                          value={item.title}
                          onChange={(e) => updatePrecaution(index, { title: e.target.value })}
                        />
                      </div>
                      <div style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 'bold' }}>내용</span>
                        <Input.TextArea
                          placeholder="유의사항 내용을 입력하세요"
                          value={item.description}
                          onChange={(e) => updatePrecaution(index, { description: e.target.value })}
                          rows={4}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      </div>
                      <Button type="link" danger style={{ padding: 0 }} onClick={() => removePrecaution(index)}>
                        삭제
                      </Button>
                    </div>
                  ))}
                  <Button type="dashed" onClick={addPrecaution} style={{ width: '100%' }}>
                    + 유의사항 추가
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={reviewsModalTitle}
        open={reviewsEditorCategoryIndex !== null}
        onCancel={() => setReviewsEditorCategoryIndex(null)}
        footer={
          <Button type="primary" onClick={() => setReviewsEditorCategoryIndex(null)}>
            닫기
          </Button>
        }
        width={720}
        style={{ top: 32 }}
        destroyOnClose>
        {reviewsModalIdx !== null && reviewsModalCategory ? (
          <div style={{ maxHeight: 'min(70vh, 560px)', overflowY: 'auto', paddingRight: 6 }}>
            <div style={{ color: '#666', fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
              각 카드에 제목, 설명, 링크 URL, 썸네일 이미지를 넣을 수 있습니다. 링크는 앱에서 링크 미리보기(OG)와 함께
              쓰일 수 있습니다. 저장 시 제목·설명·URL·이미지가 모두 비어 있는 줄은 제외됩니다.
            </div>
            {reviewsModalList.length === 0 && (
              <div
                style={{
                  color: '#999',
                  fontSize: 13,
                  textAlign: 'center',
                  padding: '24px 12px',
                  border: '1px dashed #d9d9d9',
                  borderRadius: 8,
                  marginBottom: 12,
                }}>
                아직 카드가 없습니다. 아래 버튼으로 추가하세요.
              </div>
            )}
            {reviewsModalList.map((rev, revIdx) => (
              <div
                key={`review-item-${reviewsModalIdx}-${revIdx}`}
                style={{
                  border: '1px solid #e8e8e8',
                  borderRadius: 8,
                  padding: 12,
                  marginBottom: 12,
                  background: '#fafafa',
                }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>카드 {revIdx + 1}</span>
                  <Button danger size="small" onClick={() => removeReviewFromCategory(reviewsModalIdx, revIdx)}>
                    삭제
                  </Button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>제목(계정 id/이름)</div>
                    <Input
                      size="small"
                      placeholder="계정 id/이름 입력"
                      value={rev.title}
                      onChange={(e) => updateReviewCategoryReview(reviewsModalIdx, revIdx, { title: e.target.value })}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>설명</div>
                    <Input.TextArea
                      rows={3}
                      placeholder="리뷰 설명"
                      style={{ fontSize: 13 }}
                      value={rev.description}
                      onChange={(e) =>
                        updateReviewCategoryReview(reviewsModalIdx, revIdx, {
                          description: e.target.value,
                        })
                      }
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>링크 URL</div>
                    <Input
                      size="small"
                      placeholder="https://..."
                      value={rev.link}
                      onChange={(e) => updateReviewCategoryReview(reviewsModalIdx, revIdx, { link: e.target.value })}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>이미지</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      <FileUploadButton
                        accept={IMAGE_ACCEPT}
                        style={{
                          fontSize: '12px',
                          height: '28px',
                          padding: '2px 8px',
                          borderRadius: '6px',
                          backgroundColor: '#ffffff',
                          border: '1px solid #d9d9d9',
                          color: 'rgba(0, 0, 0, 0.88)',
                          cursor: 'pointer',
                        }}
                        onChange={(e) => {
                          if (e.target.files) onChangeReviewItemImage(e.target.files, reviewsModalIdx, revIdx);
                        }}>
                        선택
                      </FileUploadButton>
                      <Button
                        danger
                        size="small"
                        onClick={() =>
                          updateReviewCategoryReview(reviewsModalIdx, revIdx, {
                            imageUrl: '',
                            imageUrlTemp: '',
                          })
                        }>
                        제거
                      </Button>
                    </div>
                    {(rev.imageUrlTemp || rev.imageUrl) && (
                      <img
                        src={cdnImageUrl(rev.imageUrlTemp || rev.imageUrl)}
                        alt=""
                        style={{
                          width: 120,
                          height: 68,
                          objectFit: 'cover',
                          borderRadius: 6,
                          border: '1px solid #eee',
                          marginTop: 8,
                          display: 'block',
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
            <Button type="dashed" block onClick={() => addReviewToCategory(reviewsModalIdx)}>
              + 리뷰 카드 추가
            </Button>
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export const CampaignsPage: React.FC = () => {
  useAuthentication();

  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Campaign>();
  const [searchQuery, setSearchQuery] = useState<{ page?: number; size?: number }>({ page: 0, size: 20 });

  const campaignsQuery = useQuery({
    queryKey: ['campaigns', JSON.stringify(searchQuery)],
    queryFn: () => api.searchCampaigns(searchQuery),
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteCampaign,
    onSuccess: async () => {
      message.success('삭제했습니다.');
      await campaignsQuery.refetch();
    },
    onError: async (e: any) => {
      message.error(e?.response?.data?.message || '삭제 중 오류가 발생했습니다.');
    },
  });

  const campaignsContainer = campaignsQuery.data?.data;

  const TABLE_COLUMNS: ColumnsType<Campaign> = [
    {
      title: 'ID',
      dataIndex: '_id',
      width: 80,
      render: (_id) => {
        return (
          <Button
            onClick={() => {
              navigator.clipboard.writeText(_id);
              toast.dark('캠페인 ID를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                bodyStyle: {
                  color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                  textAlign: 'center',
                  fontFamily: 'Pretendard',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px',
                },
                theme: 'dark',
              });
            }}>
            ID 복사
          </Button>
        );
      },
    },
    {
      title: '제목',
      width: 160,
      render: (record: Campaign) => record.title || '-',
    },
    {
      title: '활성',
      width: 90,
      render: (record: Campaign) => {
        if (record.active === false) {
          return (
            <div className="badge" style={{ backgroundColor: '#bd334a' }}>
              비활성
            </div>
          );
        }
        return (
          <div className="badge" style={{ backgroundColor: '#3fb547' }}>
            활성
          </div>
        );
      },
    },
    {
      title: '기간',
      width: 180,
      render: (record: Campaign) => {
        return (
          <div>
            <div>{record.startDate ? moment(record.startDate).format('YYYY.MM.DD HH:mm') : '-'}</div>
            <div>~</div>
            <div>{record.endDate ? moment(record.endDate).format('YYYY.MM.DD HH:mm') : '-'}</div>
          </div>
        );
      },
    },
    {
      title: '공유링크',
      width: 100,
      render: (record: Campaign) => {
        return (
          <Button
            onClick={() => {
              navigator.clipboard.writeText(`${CAMPAIGN_SHARE_BASE_URL}/${record._id}`);
              toast.dark('캠페인 URL을 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                bodyStyle: {
                  color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                  textAlign: 'center',
                  fontFamily: 'Pretendard',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px',
                },
                theme: 'dark',
              });
            }}>
            복사
          </Button>
        );
      },
    },
    {
      title: '메인 카피',
      render: (record: Campaign) => {
        return <div style={{ whiteSpace: 'pre-wrap' }}>{record.mainCopy || '-'}</div>;
      },
    },
    {
      title: '이미지',
      width: 120,
      render: (record: Campaign) => {
        const first = record.images?.[0];
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 12, color: '#666' }}>{(record.images || []).length}개</div>
            {first ? (
              <img src={cdnImageUrl(first)} style={{ width: 100, height: 56, objectFit: 'cover', borderRadius: 6 }} />
            ) : (
              <div
                style={{ width: 100, height: 56, borderRadius: 6, border: '1px solid #eee', background: '#fafafa' }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: '연동',
      width: 140,
      render: (record: Campaign) => {
        return (
          <div style={{ fontSize: 12, color: '#333' }}>
            <div>컬렉션: {record.collectionIds?.length || 0}</div>
            <div>상품: {record.productIds?.length || 0}</div>
            <div>쿠폰: {record.couponIds?.length || record.coupons?.length || 0}</div>
          </div>
        );
      },
    },
    {
      title: '생성',
      dataIndex: 'createdAt',
      width: 140,
      render: (createdAt) => (createdAt ? moment(createdAt).format('YYYY.MM.DD HH:mm') : '-'),
    },
    {
      title: '수정',
      dataIndex: 'updatedAt',
      width: 140,
      render: (updatedAt) => (updatedAt ? moment(updatedAt).format('YYYY.MM.DD HH:mm') : '-'),
    },
    {
      title: '수정',
      width: 80,
      render: (record: Campaign) => {
        return (
          <Button
            onClick={() => {
              setEditItem(record);
              setShowModal(true);
            }}>
            수정
          </Button>
        );
      },
    },
    {
      title: '삭제',
      width: 80,
      render: (record: Campaign) => {
        return (
          <Button
            danger
            loading={deleteMutation.isPending}
            onClick={() => {
              Modal.confirm({
                title: '캠페인 삭제',
                content: '삭제할까요?',
                okText: '삭제',
                cancelText: '취소',
                okButtonProps: { danger: true },
                onOk: async () => {
                  await deleteMutation.mutateAsync(record._id);
                },
              });
            }}>
            삭제
          </Button>
        );
      },
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <CampaignEditModal
        open={showModal}
        data={editItem}
        onClose={() => {
          setShowModal(false);
          setEditItem(undefined);
        }}
        needRefetch={() => {
          campaignsQuery.refetch();
        }}
      />

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>캠페인 관리</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: '#666' }}>총 {campaignsContainer?.total || 0}개</div>
          <Button
            type="primary"
            onClick={() => {
              setEditItem(undefined);
              setShowModal(true);
            }}>
            신규 캠페인 생성
          </Button>
        </div>
      </div>

      <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
        <div style={{ padding: '12px 0' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 12, color: '#666' }}>페이지 사이즈</div>
              <Switch
                checkedChildren="20"
                unCheckedChildren="50"
                checked={(searchQuery.size || 20) === 20}
                onChange={(checked) => {
                  setSearchQuery((prev) => ({ ...prev, page: 0, size: checked ? 20 : 50 }));
                }}
              />
            </div>
          </div>
        </div>

        <Table
          columns={TABLE_COLUMNS}
          scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
          style={{ whiteSpace: 'pre-wrap' }}
          rowKey={(record) => record?._id}
          dataSource={campaignsContainer?.contents || []}
          pagination={{
            current: (campaignsContainer?.page || 0) + 1,
            pageSize: campaignsContainer?.size || 20,
            total: campaignsContainer?.total || 0,
          }}
          loading={campaignsQuery.isLoading}
          onChange={(pagination) => {
            setSearchQuery((prev) => ({
              ...prev,
              page: (pagination.current || 1) - 1,
              size: pagination.pageSize || 20,
            }));
          }}
        />
      </div>
    </div>
  );
};

export default CampaignsPage;
